import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout/Layout';
import { AuthContext } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';

const Container = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 32px;
  margin-bottom: 16px;
`;

const Description = styled.p`
  color: var(--text-secondary);
  margin-bottom: 32px;
  line-height: 1.5;
`;

const PlanCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 32px;
`;

const PlanHeader = styled.div`
  margin-bottom: 24px;
`;

const PlanName = styled.h2`
  font-size: 24px;
  margin-bottom: 8px;
`;

const PlanPrice = styled.div`
  font-size: 32px;
  font-weight: 500;
  display: flex;
  align-items: baseline;
  gap: 4px;

  span {
    font-size: 16px;
    color: var(--text-secondary);
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  color: var(--text-secondary);
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 16px;
  background: var(--text-primary);
  color: var(--surface);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  width: 100%;
  padding: 16px;
  background: var(--surface);
  color: var(--text-secondary);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: var(--hover);
    color: var(--text-primary);
  }
`;

const FreeSubscription = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [isProcessing, setIsProcessing] = useState(false);

  const features = [
    'Track up to 5 subscriptions',
    'Basic tags',
    'Basic payment methods',
    'Basic analytics'
  ];

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      // First check if user has an existing subscription
      const existingSubscription = await subscriptionService.getUserSubscription(user.id);

      const subscriptionData = {
        id: existingSubscription?.id, // Include existing ID if updating
        type: 'free',
        status: 'active',
        start_date: new Date().toISOString(),
        end_date: null,
        period: null,
        price: 0,
        user_id: user.id
      };

      if (existingSubscription) {
        // Update existing subscription
        await subscriptionService.updateUserSubscription(user.id, subscriptionData);
      } else {
        // Create new subscription
        await subscriptionService.createSubscription(subscriptionData);
      }
      
      // Clear any stored subscription data from localStorage
      localStorage.removeItem('userSubscription');
      
      // Show success message
      alert('Successfully switched to Free plan');
      
      // Force a hard navigation to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error switching to free plan:', error);
      alert('Failed to switch to free plan. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Layout>
      <Container>
        <Title>Switch to Free Plan</Title>
        <Description>
          You are about to switch to the Free plan. You will lose access to Pro features at the end of your current billing period.
        </Description>

        <PlanCard>
          <PlanHeader>
            <PlanName>Free</PlanName>
            <PlanPrice>
              $0.00<span>/ forever</span>
            </PlanPrice>
          </PlanHeader>

          <FeatureList>
            {features.map((feature, index) => (
              <FeatureItem key={index}>
                <span>✓</span>
                {feature}
              </FeatureItem>
            ))}
          </FeatureList>

          <ButtonGroup>
            <ConfirmButton 
              onClick={handleConfirm}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Confirm Switch to Free Plan'}
            </ConfirmButton>
            <CancelButton onClick={() => navigate('/pro-subscription')}>
              Cancel
            </CancelButton>
          </ButtonGroup>
        </PlanCard>
      </Container>
    </Layout>
  );
};

export default FreeSubscription; 